import { useEffect, useMemo, useState } from "react";
import { Rnd } from "react-rnd";
import api, { formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Printer, Eye, EyeOff } from "lucide-react";

const PAPER_PRESETS = {
  "A4-portrait":   { width: 794,  height: 1123, label: "A4 portrait (210×297mm)" },
  "A5-portrait":   { width: 559,  height: 794,  label: "A5 portrait (148×210mm)" },
  "thermal-80mm":  { width: 302,  height: 1100, label: "Thermal 80mm × long roll" },
  "thermal-58mm":  { width: 219,  height: 900,  label: "Thermal 58mm × long roll" },
};

const MM_TO_PX = 3.78; // 96dpi
const PRESET_KEY = "eaassy_bill_custom_presets";

function loadCustomPresets() {
  try { return JSON.parse(localStorage.getItem(PRESET_KEY) || "{}"); } catch { return {}; }
}
function saveCustomPresets(p) {
  localStorage.setItem(PRESET_KEY, JSON.stringify(p));
}

const FIELD_DESCRIPTIONS = {
  org_name:    "Shop / brand name (large heading)",
  branch_name: "Branch / outlet sub-heading",
  bill_number: "Bill / invoice number",
  date:        "Bill date + time",
  customer:    "Customer name + phone",
  items_table: "Line-items table block (auto-sized rows)",
  subtotal:    "Subtotal (before GST & discount)",
  gst:         "CGST + SGST total",
  discount:    "Discount line",
  round_off:   "Round-off adjustment",
  total:       "Grand total (largest)",
  footer:      "Footer note (T&C, return policy)",
};

// Dummy preview data
const SAMPLE_BILL = {
  org_name: "My Outlet",
  branch_name: "Main Outlet",
  bill_number: "Bill #INV-1042",
  date: "12 Aug 2026 · 18:42",
  customer: "Customer: Riya Sharma · +91 98xxx 88412",
  items_table: null, // rendered specially
  subtotal: "Subtotal: ₹4,250.00",
  gst: "GST (CGST 75 + SGST 75): ₹150.00",
  discount: "Discount: −₹250.00",
  round_off: "Round-off: −₹0.20",
  total: "Total: ₹4,149.80",
  footer: null,
};

export default function BillBuilder() {
  const [layout, setLayout] = useState(null);
  const [paper, setPaper] = useState("A4-portrait");
  const [scale, setScale] = useState(0.7);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customPresets, setCustomPresets] = useState(loadCustomPresets());
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customW, setCustomW] = useState(210);
  const [customH, setCustomH] = useState(297);

  const allPresets = useMemo(() => ({ ...PAPER_PRESETS, ...customPresets }), [customPresets]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/organisation/bill-layout");
        setLayout(r.data);
        if (r.data?.page?.preset) setPaper(r.data.page.preset);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const paperDims = useMemo(() => allPresets[paper] || PAPER_PRESETS["A4-portrait"], [paper, allPresets]);

  const saveCustomPreset = () => {
    const w_mm = parseInt(customW);
    const h_mm = parseInt(customH);
    const name = customName.trim();
    if (!name || !w_mm || !h_mm) {
      toast.error("Name, width and height required");
      return;
    }
    const key = `custom-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    const dims = { width: Math.round(w_mm * MM_TO_PX), height: Math.round(h_mm * MM_TO_PX), label: `${name} (${w_mm}×${h_mm}mm)`, w_mm, h_mm, custom: true };
    const next = { ...customPresets, [key]: dims };
    setCustomPresets(next);
    saveCustomPresets(next);
    setPaper(key);
    setLayout((prev) => ({ ...prev, page: { ...prev.page, ...dims, preset: key } }));
    setShowCustomForm(false);
    setCustomName("");
    toast.success(`Custom preset "${name}" saved`);
  };

  const deleteCustomPreset = (key) => {
    const { [key]: _drop, ...rest } = customPresets;
    setCustomPresets(rest);
    saveCustomPresets(rest);
    if (paper === key) {
      setPaper("A4-portrait");
      setLayout((prev) => ({ ...prev, page: { ...prev.page, ...PAPER_PRESETS["A4-portrait"], preset: "A4-portrait" } }));
    }
    toast.success("Custom preset removed");
  };

  const updateField = (key, patch) => {
    setLayout((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.key === key ? { ...f, ...patch } : f)),
    }));
  };

  const onPaperChange = (preset) => {
    setPaper(preset);
    const dims = allPresets[preset];
    setLayout((prev) => ({ ...prev, page: { ...prev.page, ...dims, preset } }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/organisation/bill-layout", layout);
      toast.success("Bill template saved. Future bills will use this layout.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!confirm("Reset to the default bill layout? Your custom layout will be lost.")) return;
    try {
      const r = await api.post("/organisation/bill-layout/reset");
      setLayout(r.data);
      toast.success("Reset to default layout.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (loading || !layout) {
    return <div className="p-8 text-slate-500" data-testid="bill-builder-loading">Loading bill template…</div>;
  }

  const selectedField = selected ? layout.fields.find((f) => f.key === selected) : null;

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-slate-100" data-testid="bill-builder-page">
      {/* Left palette */}
      <aside className="w-72 border-r border-slate-200 bg-white p-4 overflow-y-auto" data-testid="bill-builder-palette">
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Fields</h2>
        <ul className="space-y-1">
          {layout.fields.map((f) => (
            <li key={f.key}>
              <button
                onClick={() => setSelected(f.key)}
                className={`w-full text-left px-3 py-2 text-sm border ${
                  selected === f.key ? "border-slate-900 bg-slate-50 font-semibold" : "border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`bill-field-${f.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{f.label}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); updateField(f.key, { visible: !f.visible }); }}
                    title={f.visible ? "Hide" : "Show"}
                    data-testid={`bill-toggle-${f.key}`}
                  >
                    {f.visible ? <Eye size={14} /> : <EyeOff size={14} className="text-slate-400" />}
                  </button>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{FIELD_DESCRIPTIONS[f.key]}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Canvas */}
      <main className="flex-1 overflow-auto p-6">
        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs uppercase tracking-wide text-slate-500">Paper</label>
          <select
            value={paper}
            onChange={(e) => onPaperChange(e.target.value)}
            className="h-9 border border-slate-300 px-2 text-sm"
            data-testid="bill-paper-select"
          >
            {Object.entries(allPresets).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowCustomForm((s) => !s)}
            className="h-9 px-2 border border-slate-300 text-xs hover:bg-slate-100"
            data-testid="bill-custom-toggle"
            title="Define a custom paper size"
          >
            + Custom
          </button>
          {paper.startsWith("custom-") && (
            <button
              onClick={() => deleteCustomPreset(paper)}
              className="h-9 px-2 border border-rose-300 text-xs text-rose-700 hover:bg-rose-50"
              data-testid="bill-custom-delete"
              title="Delete this custom preset"
            >
              Delete preset
            </button>
          )}
          <label className="text-xs uppercase tracking-wide text-slate-500 ml-4">Zoom</label>
          <input
            type="range"
            min="0.3"
            max="1.2"
            step="0.05"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            data-testid="bill-zoom"
          />
          <span className="text-xs text-slate-600">{Math.round(scale * 100)}%</span>
          <div className="ml-auto flex gap-2">
            <button onClick={onReset} className="h-9 px-3 border border-slate-300 text-sm hover:bg-slate-100 inline-flex items-center gap-1" data-testid="bill-reset-btn">
              <RotateCcw size={14} /> Reset
            </button>
            <button onClick={onSave} disabled={saving} className="h-9 px-4 bg-slate-900 text-white text-sm hover:bg-slate-800 inline-flex items-center gap-1 disabled:opacity-50" data-testid="bill-save-btn">
              <Save size={14} /> {saving ? "Saving…" : "Save Layout"}
            </button>
          </div>
        </div>

        {showCustomForm && (
          <div className="mb-4 p-3 border border-slate-300 bg-slate-50 flex flex-wrap items-end gap-2" data-testid="bill-custom-form">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500 block">Preset name</label>
              <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="e.g. Thermal-110x200" className="h-8 px-2 border border-slate-300 text-sm w-48" data-testid="bill-custom-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500 block">Width (mm)</label>
              <input type="number" value={customW} onChange={(e) => setCustomW(e.target.value)} className="h-8 px-2 border border-slate-300 text-sm w-24" data-testid="bill-custom-w" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500 block">Height (mm)</label>
              <input type="number" value={customH} onChange={(e) => setCustomH(e.target.value)} className="h-8 px-2 border border-slate-300 text-sm w-24" data-testid="bill-custom-h" />
            </div>
            <button onClick={saveCustomPreset} className="h-8 px-3 bg-slate-900 text-white text-sm hover:bg-slate-800" data-testid="bill-custom-save">Save preset</button>
            <button onClick={() => setShowCustomForm(false)} className="h-8 px-3 border border-slate-300 text-sm hover:bg-slate-100">Cancel</button>
            <span className="text-[11px] text-slate-500 ml-auto">Saved presets persist on this device.</span>
          </div>
        )}

        <div
          className="relative bg-white shadow-md mx-auto border border-slate-300"
          style={{
            width: paperDims.width * scale,
            height: paperDims.height * scale,
          }}
          onClick={() => setSelected(null)}
          data-testid="bill-canvas"
        >
          <div
            style={{
              width: paperDims.width,
              height: paperDims.height,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            {layout.fields.filter((f) => f.visible).map((f) => (
              <Rnd
                key={f.key}
                position={{ x: f.x, y: f.y }}
                size={{ width: f.w, height: f.h }}
                onDragStop={(e, d) => updateField(f.key, { x: Math.round(d.x), y: Math.round(d.y) })}
                onResizeStop={(_e, _dir, ref, _delta, position) =>
                  updateField(f.key, {
                    w: parseInt(ref.style.width),
                    h: parseInt(ref.style.height),
                    x: Math.round(position.x),
                    y: Math.round(position.y),
                  })
                }
                bounds="parent"
                onClick={(e) => { e.stopPropagation(); setSelected(f.key); }}
                className={`flex items-center px-2 cursor-move ${
                  selected === f.key ? "outline-2 outline-blue-600 bg-blue-50/60" : "outline outline-1 outline-dashed outline-slate-400 hover:outline-slate-600"
                }`}
                style={{ outlineStyle: selected === f.key ? "solid" : "dashed" }}
                data-testid={`bill-rnd-${f.key}`}
              >
                <FieldPreview field={f} layout={layout} />
              </Rnd>
            ))}
          </div>
        </div>
      </main>

      {/* Right properties */}
      <aside className="w-72 border-l border-slate-200 bg-white p-4 overflow-y-auto" data-testid="bill-properties">
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Properties</h2>
        {!selectedField ? (
          <p className="text-sm text-slate-500">Select a field on the canvas to edit its size, font, and alignment.</p>
        ) : (
          <div className="space-y-3" data-testid="bill-props-form">
            <div>
              <div className="text-xs font-medium text-slate-700">{selectedField.label}</div>
              <div className="text-[11px] text-slate-500">{FIELD_DESCRIPTIONS[selectedField.key]}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="X" value={selectedField.x} onChange={(v) => updateField(selectedField.key, { x: v })} testid="prop-x" />
              <NumberField label="Y" value={selectedField.y} onChange={(v) => updateField(selectedField.key, { y: v })} testid="prop-y" />
              <NumberField label="Width" value={selectedField.w} onChange={(v) => updateField(selectedField.key, { w: v })} testid="prop-w" />
              <NumberField label="Height" value={selectedField.h} onChange={(v) => updateField(selectedField.key, { h: v })} testid="prop-h" />
              <NumberField label="Font size" value={selectedField.font_size} onChange={(v) => updateField(selectedField.key, { font_size: v })} testid="prop-fontsize" />
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Align</label>
                <select
                  value={selectedField.align}
                  onChange={(e) => updateField(selectedField.key, { align: e.target.value })}
                  className="h-8 border border-slate-300 px-2 text-xs w-full"
                  data-testid="prop-align"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={selectedField.bold}
                onChange={(e) => updateField(selectedField.key, { bold: e.target.checked })}
                data-testid="prop-bold"
              />
              Bold
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={selectedField.visible}
                onChange={(e) => updateField(selectedField.key, { visible: e.target.checked })}
                data-testid="prop-visible"
              />
              Visible
            </label>
          </div>
        )}
        <div className="mt-6 border-t border-slate-200 pt-4">
          <label className="text-xs text-slate-500 block mb-1">Footer text</label>
          <textarea
            value={layout.footer_text || ""}
            onChange={(e) => setLayout({ ...layout, footer_text: e.target.value })}
            className="w-full border border-slate-300 p-2 text-xs h-20"
            data-testid="prop-footer-text"
          />
        </div>
        <div className="mt-6 text-[11px] text-slate-500 leading-relaxed">
          Sample data shown in preview. The saved layout drives the printed customer bill — drag-resize each field, hide what you don't need, then click Save Layout. Use browser <Printer size={12} className="inline" /> Print from the Bill Detail page to verify.
        </div>
      </aside>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] text-slate-500 block mb-1">{label}</label>
      <input
        type="number"
        value={value || 0}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className="h-8 border border-slate-300 px-2 text-xs w-full"
        data-testid={testid}
      />
    </div>
  );
}

function FieldPreview({ field, layout }) {
  const style = {
    fontSize: field.font_size,
    fontWeight: field.bold ? 700 : 400,
    textAlign: field.align,
    width: "100%",
    pointerEvents: "none",
    userSelect: "none",
    lineHeight: 1.2,
  };
  if (field.key === "items_table") {
    return (
      <div style={{ ...style, fontSize: field.font_size, textAlign: "left" }}>
        <div style={{ borderBottom: "1px solid #cbd5e1", paddingBottom: 4, fontWeight: 600 }}>
          Item · Qty · Rate · Total
        </div>
        <div>Aaria Silk Suit · 1 · ₹2,150.00 · ₹2,150.00</div>
        <div>Pink Anarkali Set · 2 · ₹1,050.00 · ₹2,100.00</div>
        <div style={{ color: "#94a3b8", fontSize: field.font_size - 1, fontStyle: "italic" }}>… more rows render here at print time</div>
      </div>
    );
  }
  if (field.key === "footer") {
    return <div style={style}>{layout.footer_text || "Thank you for your visit!"}</div>;
  }
  return <div style={style}>{SAMPLE_BILL[field.key] || field.label}</div>;
}
